export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function withTimeout<T>(promise: Promise<T>, ms: number, label?: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`Timeout after ${ms}ms${label ? ': ' + label : ''}`)), ms)
  })
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId))
}

/**
 * 生成指定长度的消息唯一标识符
 * @param length 标识符长度
 * @returns 生成的唯一标识符
 */
export function generateMessageID(length: number = 16): string {
  let arr: string[] = []
  let res: string[] = []
  for (var i = 65; i < 91; i++) {
    arr.push(String.fromCharCode(i))
  }
  for (var i = 0; i < 10; i++) {
    arr.push(i.toString())
  }
  for (let i = 0; i < length; i++) {
    let numb: number = Math.ceil(arr.length * Math.random() - 1)
    res.push(arr[numb])
  }
  return res.join('')
}

export const printHex = (data: number[], prefix?: string, limit?: number) => {
  let str = ''
  if (prefix) str = prefix + ': '
  for (let index = 0; index < data.length; index++) {
    const element = data[index]
    str += element.toString(16).padStart(2, '0') + ' '
    if (limit && index > limit) {
      break
    }
  }
  console.log(str)
}

export const fitBytes = (data: number[], tpl: number[]): boolean => {
  let ret = true
  for (let i = 0; i < tpl.length; i++) {
    if (data[i] !== tpl[i]) {
      ret = false
      break
    }
  }
  return ret
}

export const bytes64 = (init: number[] = []) => {
  let res = []
  for (let i = 0; i < 64; i++) {
    res.push(init[i] || 0)
  }
  return res
}

export const bytesPadding64 = (data: number[], max = 64, fixed?: number[]): number[] => {
  let res = []
  if (!fixed) {
    for (let i = 0; i < max; i++) {
      res.push(data[i] || 0)
    }
    return res
  }
  while (data.length <= max - fixed.length) {
    data.push(...fixed)
  }
  return data
}

export const makeData64WithCRC = (data: number[], end?: number) => {
  if (end != undefined) {
    data[3] = (0x35 + data[0] + data[1] + data[2] + end) & 0xff
  } else {
    data[3] = (0x35 + data[0] + data[1] + data[2] + data[data.length - 1]) & 0xff
  }
  return bytesPadding64(data)
}

export const makeDataWithCRC = (data: number[], end?: number) => {
  if (end != undefined) {
    return (0x35 + data[0] + data[1] + data[2] + end) & 0xff
  } else {
    return (0x35 + data[0] + data[1] + data[2] + data[data.length - 1]) & 0xff
  }
}

/**
 * 使用 2 字节编码：低字节 = row，高字节 = col
 * 支持 row, col [0, 255]
 */
export const encodeRowColNumber = (row: number, col: number): number => {
  if (!Number.isInteger(row) || !Number.isInteger(col)) {
    throw new Error('row and col must be integers')
  }
  if (row < 0 || row > 255 || col < 0 || col > 255) {
    throw new Error('row and col must be in range [0, 255] (8-bit unsigned)')
  }
  return (col << 8) | row
}

/**
 * 解码：从 2 字节编码中提取 [row, col]
 * 返回 [row, col]
 */
export const decodeRowColNumber = (num: number): [number, number] => {
  const row = num & 0xff // 取低 8 位
  const col = (num >> 8) & 0xff // 取高 8 位
  return [row, col]
}

export const numberToRgb = (num: number): { r: number; g: number; b: number } => {
  const r = (num >> 16) & 0xff
  const g = (num >> 8) & 0xff
  const b = num & 0xff
  return { r, g, b }
}

export function reverseMap(originalMap: { [index: number]: number }): { [index: number]: number } {
  return Object.fromEntries(Object.entries(originalMap).map(([key, value]) => [value, Number(key)])) as { [index: number]: number }
}

export function transpose<T>(matrix: T[][]): T[][] {
  if (matrix.length === 0) return []
  const colCount = matrix[0].length
  return Array.from({ length: colCount }, (_, i) => matrix.map((row) => row[i]))
}

export const alignToMultiple = (value: number, nearest: number, mode: string = 'round') => {
  if (nearest <= 0) {
    throw new Error('nearest must be a positive number')
  }
  const division = value / nearest

  let result: number
  switch (mode) {
    case 'floor':
      result = Math.floor(division)
      break
    case 'ceil':
      result = Math.ceil(division)
      break
    case 'round':
    default:
      result = Math.round(division)
      break
  }

  return result * nearest
}
